import { BunjaStoreGetResult, bunja } from 'bunja';
import { BunjaStoreContext, createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';
import { getHook } from 'react-bunja-devtools/hook';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { devtoolsRouteBunja, readRouteParams } from '../../../state';

const BunjaStoreScope = createScopeFromContext(BunjaStoreContext);

export const dagDevtoolsBunja = bunja(() => {
  const hook = getHook()!;
  const bunjaStore = bunja.use(BunjaStoreScope);
  const store = bunja.use(JotaiStoreScope);
  const { routeAtom } = bunja.use(devtoolsRouteBunja);

  const bunjasAtom = atom({ ...hook.bunjas });
  bunja.effect(() => hook.on('bunjaCreated', () => store.set(bunjasAtom, { ...hook.bunjas })));
  const scopesAtom = atom({ ...hook.scopes });
  bunja.effect(() => hook.on('scopeCreated', () => store.set(scopesAtom, { ...hook.scopes })));

  const selectedBunjaIdAtom = atom((get) => {
    const route = get(routeAtom);
    return readRouteParams(route, 'dag')?.selectedBunjaId;
  });

  return { hook, bunjaStore, bunjasAtom, scopesAtom, selectedBunjaIdAtom };
});
dagDevtoolsBunja.debugLabel = 'dagDevtoolsBunja';

export const SelectedBunjaIdContext = createContext<string>(null!);
export const SelectedBunjaIdScope = createScopeFromContext(SelectedBunjaIdContext);

export const bunjaInstancesBunja = bunja(() => {
  const { hook, bunjaStore, bunjasAtom } = bunja.use(dagDevtoolsBunja);
  const store = bunja.use(JotaiStoreScope);

  const selectedBunjaId = bunja.use(SelectedBunjaIdScope);

  type BunjaInstance = NonNullable<BunjaStoreGetResult<unknown>['bunjaInstance']>;
  const instancesAtom = atom<Record<string, BunjaInstance>>({});

  // Load initial instances for selected bunja
  bunja.effect(() => {
    const allBunjas = bunjaStore._internalState?.bunjas || {};
    const matchingInstances: Record<string, BunjaInstance> = {};

    Object.entries(allBunjas).forEach(([instanceId, instance]) => {
      const bunjaId = instance.id.split(':')[0];
      if (bunjaId === selectedBunjaId) {
        matchingInstances[instanceId] = instance;
      }
    });

    store.set(instancesAtom, matchingInstances);
  });

  bunja.effect(() => {
    hook.on('bunjaInstanceMounted', ({ storeId, bunjaInstanceId }) => {
      if (storeId !== bunjaStore.id) return;
      const instance = bunjaStore._internalState?.bunjas[bunjaInstanceId];
      if (!instance) return;
      const bunjaId = instance.id.split(':')[0];
      if (bunjaId !== selectedBunjaId) return;
      store.set(instancesAtom, { ...store.get(instancesAtom), [bunjaInstanceId]: instance });
    });
  });
  hook.on('bunjaInstanceUnmounted', ({ storeId, bunjaInstanceId }) => {
    if (storeId !== bunjaStore.id) return;
    store.set(instancesAtom, (oldInstances) => {
      if (!(bunjaInstanceId in oldInstances)) return oldInstances;
      const newInstances = { ...oldInstances };
      delete newInstances[bunjaInstanceId];
      return newInstances;
    });
  });

  return { bunjasAtom, instancesAtom };
});
bunjaInstancesBunja.debugLabel = 'bunjaInstancesBunja';
